import { Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LegalLayout } from "@/components/legal/LegalLayout";
import { openCookieSettings } from "@/components/CookieConsent";
import { BUSINESS } from "@/lib/legal";

const COOKIE_COLUMNS = ["Nombre", "Titular", "Tipo", "Finalidad", "Duración"];

const COOKIES = [
  ["session_token / access_token", "Propia", "Técnica (necesaria)", "Mantiene la sesión iniciada del cliente o del personal de administración.", "7 días / 12 horas"],
  ["ozono_cookie_consent", "Propia (almacenamiento local)", "Técnica (necesaria)", "Recuerda la elección realizada en el aviso de cookies.", "Hasta que se borre el navegador"],
  ["ozono_install_dismissed", "Propia (almacenamiento local)", "Técnica (necesaria)", "Recuerda que ha cerrado el aviso de instalación de la aplicación.", "Hasta que se borre el navegador"],
  ["googtrans", "Google Translate", "Preferencias", "Guarda el idioma elegido para traducir la web. Solo se crea si selecciona un idioma distinto al español.", "Sesión"],
  ["__stripe_mid, __stripe_sid", "Stripe", "Técnica (necesaria para el pago)", "Prevención del fraude y funcionamiento de la pasarela de pago. Se instalan en la página de pago de Stripe.", "1 año / 30 minutos"],
  ["NID, CONSENT y similares", "Google Maps", "Técnica de terceros", "Necesarias para mostrar el mapa de ubicación del local incrustado en la web.", "6 meses – 2 años"],
  ["IDE, __gads, __gpi, test_cookie, DSID", "Google AdSense (google.com / doubleclick.net)", "Publicitaria", "Mostrar anuncios de Google AdSense y medir su rendimiento. Si no aceptas todas las cookies, los anuncios se sirven sin personalizar y no se crea un perfil publicitario.", "1 día – 13 meses"],
];

const sections = [
  {
    id: "que-son",
    title: "¿Qué son las cookies?",
    content: (
      <p>
        Las cookies son pequeños archivos que se almacenan en su dispositivo al visitar una web y que permiten, entre otras cosas,
        recordar sus preferencias o mantener su sesión iniciada. En esta política incluimos también otras tecnologías similares, como el
        almacenamiento local del navegador (<em>localStorage</em>).
      </p>
    ),
  },
  {
    id: "cookies-usadas",
    title: "Cookies que utiliza este sitio",
    content: (
      <>
        <p>
          <span className="notranslate">{BUSINESS.name}</span> emplea cookies técnicas imprescindibles, una cookie de preferencias
          de idioma y <strong>cookies publicitarias de Google AdSense</strong>. Las publicitarias solo se utilizan para anuncios
          personalizados si pulsas «Aceptar todas»; en caso contrario Google muestra anuncios no personalizados (basados únicamente
          en el contenido de la página). Puedes consultar cómo usa Google los datos en{" "}
          <a className="text-cyan-700 underline underline-offset-2" href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">
            policies.google.com/technologies/partner-sites
          </a>:
        </p>
        <div className="overflow-x-auto rounded-xl border border-slate-200" data-testid="cookies-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                {COOKIE_COLUMNS.map((c) => <th key={c} className="px-4 py-2.5 font-semibold">{c}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {COOKIES.map((r) => (
                <tr key={r[0]} className="align-top">
                  {r.map((c, i) => (
                    <td key={COOKIE_COLUMNS[i]} className={`px-4 py-3 text-slate-600 ${i === 0 ? "font-mono text-xs notranslate" : ""}`}>{c}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    ),
  },
  {
    id: "gestionar",
    title: "Cómo gestionar o desactivar las cookies",
    content: (
      <>
        <p>
          Puede cambiar su elección en cualquier momento con el botón de abajo. Además, puede bloquear o eliminar las cookies desde la
          configuración de su navegador:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li><a className="text-cyan-700 underline underline-offset-2" href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
          <li><a className="text-cyan-700 underline underline-offset-2" href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
          <li><a className="text-cyan-700 underline underline-offset-2" href="https://support.apple.com/es-es/HT201265" target="_blank" rel="noopener noreferrer">Safari (iPhone, iPad y Mac)</a></li>
          <li><a className="text-cyan-700 underline underline-offset-2" href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
        </ul>
        <p>Tenga en cuenta que si bloquea las cookies técnicas, algunas funciones (inicio de sesión, pago) podrían no funcionar correctamente.</p>
        <Button type="button" variant="outline" onClick={openCookieSettings} data-testid="cookie-reopen-btn" className="rounded-full">
          <Settings2 className="h-4 w-4 mr-2" /> Cambiar mi elección de cookies
        </Button>
      </>
    ),
  },
  {
    id: "actualizaciones",
    title: "Actualizaciones",
    content: (
      <p>
        Esta Política de cookies puede modificarse cuando cambien las cookies utilizadas o la normativa aplicable. Le recomendamos
        revisarla periódicamente.
      </p>
    ),
  },
];

export default function PoliticaCookies() {
  return (
    <LegalLayout
      testId="legal-cookies-page"
      title="Política de cookies"
      subtitle="Qué cookies usamos, para qué sirven y cómo puedes gestionarlas."
      sections={sections}
    />
  );
}
